import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { guard, cardinalApi } from '../services/auth.api'
import { User, UserFilters, randomUuid } from '@icure/cardinal-sdk'

const PATIENT_INDEX_TAG = { type: 'User' as const, id: 'patientIndex' }

export const userApiRtk = createApi({
  reducerPath: 'userApi',
  tagTypes: ['User'],
  baseQuery: fetchBaseQuery({
    baseUrl: '',
  }),
  endpoints: (builder) => ({
    createRecoveryInfoForAvailableKeyPairs: builder.mutation<string | undefined, void>({
      async queryFn(_, { getState }) {
        const recoveryApi = (await cardinalApi(getState))?.recovery
        return guard([recoveryApi], async ([recoveryApi]): Promise<string | undefined> => {
          return (await recoveryApi.createRecoveryInfoForAvailableKeyPairs({ includeParentsKeys: true }))
            .asBase32()
            .match(/.{1,4}/g)
            ?.join('-')
        })
      },
    }),
    getUser: builder.query<User | undefined, string>({
      async queryFn(id, { getState }) {
        const userApi = (await cardinalApi(getState))?.user
        return guard([userApi], async ([userApi]): Promise<User> => {
          const user = await userApi.getUser(id)
          if (!user) {
            throw new Error('User does not exist')
          }
          return user
        })
      },
      providesTags: (res) => (res ? [{ type: 'User', id: res.id }] : []),
    }),
    createUser: builder.mutation<User | undefined, { email: string; id: string; name: string }>({
      async queryFn({ email, id, name }, { getState }) {
        const userApi = (await cardinalApi(getState))?.user
        return guard([userApi], async ([userApi]): Promise<User> => {
          const createdUser = await userApi.createUser(
            new User({
              id: randomUuid(),
              name: name,
              email: email,
              patientId: id,
            }),
          )
          if (!createdUser) {
            throw new Error('User does not exist')
          }
          return new User(createdUser)
        })
      },
      invalidatesTags: (result) => (!!result ? [{ type: 'User', id: 'all' }, { type: 'User', id: result.id }, PATIENT_INDEX_TAG] : []),
    }),
    getPatientIdsWithUserAccount: builder.query<string[] | undefined, void>({
      async queryFn(_arg, { getState }) {
        const userApi = (await cardinalApi(getState))?.user
        return guard([userApi], async ([userApi]): Promise<string[]> => {
          const iterator = await userApi.filterUsersBy(UserFilters.all())
          const patientIds = new Set<string>()
          while (await iterator.hasNext()) {
            const batch = await iterator.next(500)
            for (const u of batch) {
              if (u.patientId) patientIds.add(u.patientId)
            }
          }
          return Array.from(patientIds)
        })
      },
      providesTags: [PATIENT_INDEX_TAG],
    }),
    ensureUserForPatient: builder.mutation<{ created: boolean; userId?: string } | undefined, { email: string; patientId: string; name: string }>({
      async queryFn({ email, patientId, name }, { getState }) {
        const userApi = (await cardinalApi(getState))?.user
        return guard([userApi], async ([userApi]): Promise<{ created: boolean; userId?: string }> => {
          const existing = await userApi.findByPatientId(patientId)
          if (existing.length > 0) {
            return { created: false, userId: existing[0] }
          }
          const created = await userApi.createUser(
            new User({
              id: randomUuid(),
              name,
              email,
              patientId,
            }),
          )
          return { created: true, userId: created?.id }
        })
      },
      invalidatesTags: (result) => (result?.created ? [{ type: 'User', id: 'all' }, PATIENT_INDEX_TAG] : []),
    }),
  }),
})

export const { useGetUserQuery, useCreateUserMutation, useCreateRecoveryInfoForAvailableKeyPairsMutation, useGetPatientIdsWithUserAccountQuery, useEnsureUserForPatientMutation } =
  userApiRtk
